import Phaser from 'phaser';
import { GameScene, CLIENT_OPTIONS_STORAGE_KEY, type SavedClientOptions } from '../GameScene';
import { TargetPanel } from '../ui/TargetPanel';
import type { PlayerStateView, NPCStateView } from '../../state/GameStateView';
import { EntityState } from '@shared/types/entity';
import { TILE_SIZE, VIEW_HALF_RANGE_X, VIEW_HALF_RANGE_Y } from '@shared/constants';
import { getNPCTemplate } from '@shared/data/npcs';
import { getNpcTypeIconName } from '../../sprites/IconAtlases';
import { getBodyFrameDimensions } from '../../sprites/BodySpriteManager';

/**
 * Sistema de target-lock (paradigma WoW), extraído de GameScene (god-object). Posee el estado del
 * target seleccionado + retícula + panel, y toda la lógica de set/clear/cycle/sync-con-servidor.
 * Accede a la escena (entidades, red, opciones) vía la referencia `scene`. Ver docs/TARGETING.md.
 *
 * NOTA: los métodos y campos conservan sus nombres originales para que GameScene delegue con un
 * simple `this.targets.<x>`. Las refs a miembros de la escena se reescribieron a `this.scene.<x>`.
 */
export class TargetController {
  selectedTargetId: string | null = null;
  selectedTargetType: 'player' | 'npc' | null = null;
  lastObservedTargetId: string = '';
  targetReticle: Phaser.GameObjects.Sprite | null = null;
  targetPanel: TargetPanel | null = null;
  lastReticleBodyId: number | null = null;
  autoRetargetOnDeath: boolean = true;
  lastAutoRetargetFor: string | null = null;

  constructor(private scene: GameScene) {}

  createTargetPanelUI(): void {
    const saved = this.scene.persistence.loadClientOptions();
    const { width } = this.scene.scale;

    const defaultWidth = 220;
    const defaultHeight = 70;
    const initialWidth = Math.max(160, Math.min(saved?.targetPanelWidth ?? defaultWidth, width - 4));
    const initialHeight = saved?.targetPanelHeight ?? defaultHeight;
    const initialX = saved?.targetPanelX ?? Math.floor(width / 2 - initialWidth / 2);
    const initialY = saved?.targetPanelY ?? 18;

    this.targetPanel = new TargetPanel({
      scene: this.scene,
      initialX,
      initialY,
      initialWidth,
      initialHeight,
      minWidth: 160,
      minHeight: 56,
      onPositionSizeChange: (x, y, w, h) => this.saveTargetPanelState(x, y, w, h),
    });
  }

  private saveTargetPanelState(x: number, y: number, w: number, h: number): void {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(CLIENT_OPTIONS_STORAGE_KEY);
      const existing = raw ? (JSON.parse(raw) as Partial<SavedClientOptions>) : {};
      const data: Partial<SavedClientOptions> = {
        ...existing,
        targetPanelX: x,
        targetPanelY: y,
        targetPanelWidth: w,
        targetPanelHeight: h,
      };
      window.localStorage.setItem(CLIENT_OPTIONS_STORAGE_KEY, JSON.stringify(data));
    } catch {
      // ignore
    }
  }

  /**
   * Pide al server lockear un target. Idempotente: si ya es el mismo, no manda nada.
   * El target NO se setea hasta que el server lo confirma vía PlayerState.targetId.
   */
  setTarget(_type: 'player' | 'npc', id: string): void {
    if (this.selectedTargetId === id) return;
    if (!this.scene.colyseusClient) return;
    this.scene.colyseusClient.send({
      type: 'select_target' as any,
      targetId: id,
    } as any);
  }

  /**
   * Pide al server limpiar el target. Idempotente.
   */
  clearTarget(): void {
    if (!this.selectedTargetId) return;
    if (!this.scene.colyseusClient) return;
    this.scene.colyseusClient.send({
      type: 'select_target' as any,
      targetId: null,
    } as any);
  }

  /**
   * Reacciona a un cambio del targetId en el schema (server confirmó / cleanup).
   * Es el ÚNICO lugar donde se actualizan selectedTargetId/Type y se crea/destruye
   * el reticle + panel.
   */
  private applyServerTarget(serverTargetId: string): void {
    // Cualquier cambio real de target resetea el guard de auto-retarget
    // (permite que la próxima muerte vuelva a disparar el ciclo).
    if (serverTargetId !== this.selectedTargetId) {
      this.lastAutoRetargetFor = null;
    }

    if (serverTargetId === '') {
      // Server limpió el target (rechazo, despawn, out-of-range). El label del NPC
      // que estaba seleccionado se apaga solo (updateNpcNameVisibility deja de verlo
      // como `selected`).
      this.selectedTargetId = null;
      this.selectedTargetType = null;
      this.lastReticleBodyId = null;
      if (this.targetReticle) {
        this.targetReticle.destroy();
        this.targetReticle = null;
      }
      this.targetPanel?.hide();
      return;
    }

    // Cambio de target a otra entity: el label del anterior se apaga solo vía
    // updateNpcNameVisibility (ya no será `selected`).
    this.lastReticleBodyId = null;

    // Determinar tipo mirando ambas colecciones locales (NPCs primero por consistencia).
    let type: 'player' | 'npc' | null = null;
    if (this.scene.npcObjects.has(serverTargetId)) type = 'npc';
    else if (this.scene.playerObjects.has(serverTargetId)) type = 'player';

    if (!type) {
      // El server confirmó un target pero el cliente todavía no lo ve renderizado.
      // Esperamos al próximo tick — updateTargetReticle reintenta cada frame.
      this.selectedTargetId = serverTargetId;
      this.selectedTargetType = null;
      return;
    }

    this.selectedTargetId = serverTargetId;
    this.selectedTargetType = type;

    const data = this.readTargetSnapshot();
    if (!data) return;

    this.ensureTargetReticle(data.bodyId);

    // Boss flag + ícono de tipo: lookup local del template via templateId del NPCState.
    // Cero roundtrip — los templates están en shared/data/npcs.ts.
    let isBoss = false;
    let typeIconName: string | undefined;
    if (type === 'npc' && this.scene.room) {
      const npcState = this.scene.room.state.npcs.get(serverTargetId) as NPCStateView | undefined;
      const templateId = npcState?.templateId;
      if (templateId) {
        const tmpl = getNPCTemplate(templateId);
        isBoss = tmpl?.isBoss === true;
        typeIconName = getNpcTypeIconName(tmpl) ?? undefined;
      }
    }

    this.targetPanel?.show({
      id: serverTargetId,
      type,
      name: data.name,
      headId: data.headId,
      bodyId: data.bodyId,
      isPlayer: type === 'player',
      currentHp: data.currentHp,
      maxHp: data.maxHp,
      isBoss,
      typeIconName,
    });
  }

  private ensureTargetReticle(bodyId: number): void {
    if (!this.scene.textures.exists(GameScene.DROPSHADOW_TEXTURE_KEY)) return;
    if (!this.targetReticle) {
      this.targetReticle = this.scene.add.sprite(0, 0, GameScene.DROPSHADOW_TEXTURE_KEY);
      this.targetReticle.setOrigin(0.5, 0.5);
      // setTintFill REEMPLAZA los píxeles oscuros del dropshadow por color sólido
      // (preserva alpha). Convierte la sombra negra en una elipse amarilla brillante.
      this.targetReticle.setTintFill(0xffaa00);
      this.targetReticle.setAlpha(0.9);
      this.targetReticle.setScrollFactor(1);
    }
    this.rescaleTargetReticle(bodyId);
  }

  private rescaleTargetReticle(bodyId: number): void {
    if (!this.targetReticle) return;
    const { width: bodyWidth } = getBodyFrameDimensions(bodyId);
    // 0.90 = 1.20 × 0.75: rodea al body con un halo fino sin pasarse.
    // (Dropshadow real está en 0.75 — esto queda apenas más grande.)
    const scaleX = (bodyWidth / GameScene.DROPSHADOW_TEXTURE_WIDTH) * 0.90;
    const scaleY = scaleX;
    this.targetReticle.setScale(scaleX, scaleY);
    // Reiniciar el pulso desde el nuevo baseline.
    this.startReticlePulse();
  }

  /**
   * Única autoridad per-frame de la visibilidad del nombre (label + typeIcon) de
   * cada NPC. Reglas:
   *  - Muerto: SÓLO el sacerdote (priestService), y sólo si está en hover o seleccionado.
   *  - Vivo: el NPC seleccionado (target) siempre; en modo 'always' todos; en modo
   *    'hover' sólo el que esté bajo el cursor.
   * El flag `nameHovered` lo setean los handlers del tileZone (pointerover/out).
   */
  updateNpcNameVisibility(): void {
    const dead = this.scene.isLocalPlayerDead();
    this.scene.npcObjects.forEach((render, id) => {
      // Modo según tipo de NPC (neutral vs hostil); ambos default 'hover'.
      const mode = render.hostile
        ? this.scene.hostileNpcNameVisibilityMode
        : this.scene.npcNameVisibilityMode;
      const alwaysMode = mode === 'always';
      const hovered = render.nameHovered === true;
      const selected = this.selectedTargetType === 'npc' && this.selectedTargetId === id;
      const show = dead
        ? this.scene.isPriestNpc(id) && (hovered || selected)
        : selected || alwaysMode || hovered;
      const a = show ? 1 : 0;
      if (render.label.visible !== show) render.label.setVisible(show);
      if (render.label.alpha !== a) render.label.setAlpha(a);
      if (render.typeIcon) {
        if (render.typeIcon.visible !== show) render.typeIcon.setVisible(show);
        if (render.typeIcon.alpha !== a) render.typeIcon.setAlpha(a);
      }
    });
  }

  /**
   * Mata cualquier tween activo del reticle y deja alpha fijo.
   * Decisión del usuario: sin pulse (ni alpha ni escala).
   * Mantengo la función como un único punto de reset para si en el futuro
   * volvemos a animar algo.
   */
  private startReticlePulse(): void {
    if (!this.targetReticle) return;
    this.scene.tweens.killTweensOf(this.targetReticle);
    this.targetReticle.setAlpha(0.9);
  }

  /**
   * Lee la posición y datos vivos del target actual desde room.state.
   * Devuelve null si el target ya no existe (= salió de visión / despawn).
   */
  private readTargetSnapshot(): null | {
    worldX: number;
    worldY: number;
    serverTileX: number;
    serverTileY: number;
    bodyId: number;
    name: string;
    headId: number;
    currentHp: number;
    maxHp: number;
    state: string;
  } {
    if (!this.selectedTargetId || !this.selectedTargetType || !this.scene.room) return null;

    if (this.selectedTargetType === 'player') {
      const player = this.scene.room.state.players.get(this.selectedTargetId) as PlayerStateView | undefined;
      if (!player) return null;
      const render = this.scene.playerObjects.get(this.selectedTargetId);
      const worldX = render?.currentWorldX ?? (player.x * TILE_SIZE + TILE_SIZE / 2);
      const worldY = render?.currentWorldY ?? (player.y * TILE_SIZE + TILE_SIZE / 2);
      const bodyId = render?.bodyId ?? (player.bodyId || player.nakedBodyId || 1);
      return {
        worldX,
        worldY,
        serverTileX: player.x,
        serverTileY: player.y,
        bodyId,
        name: player.name ?? 'Jugador',
        headId: player.headId ?? 0,
        currentHp: player.minHp ?? 0,
        maxHp: player.maxHp ?? 1,
        state: player.state ?? EntityState.IDLE,
      };
    }

    // npc
    const npc = this.scene.room.state.npcs.get(this.selectedTargetId) as NPCStateView | undefined;
    if (!npc) return null;
    const render = this.scene.npcObjects.get(this.selectedTargetId);
    const worldX = render?.currentWorldX ?? (npc.x * TILE_SIZE + TILE_SIZE / 2);
    const worldY = render?.currentWorldY ?? (npc.y * TILE_SIZE + TILE_SIZE / 2);
    const bodyId = render?.bodyId ?? (npc.bodyId || 1);
    return {
      worldX,
      worldY,
      serverTileX: npc.x,
      serverTileY: npc.y,
      bodyId,
      name: npc.name ?? 'NPC',
      headId: npc.headId ?? 0,
      currentHp: npc.minHp ?? 0,
      maxHp: npc.maxHp ?? 1,
      state: npc.state ?? EntityState.IDLE,
    };
  }

  /**
   * Busca el entity (NPC con prioridad, luego player) que ocupa el tile clickeado.
   * Ignora al propio jugador local.
   */
  /**
   * Encuentra la entidad (NPC o player) bajo el punto de mundo clickeado,
   * testeando contra la CAJA VISIBLE del body — no contra el tile lógico.
   *
   * Por qué: el sprite del body está anclado a los pies (origin 0.5,1) y mide
   * ~64px de alto, así que el cuerpo/cabeza se dibujan ~2 tiles ARRIBA del tile
   * lógico. El match exacto por tile sólo acertaba al clickear los pies; clickear
   * el torso/cabeza fallaba (patrón "a veces sí, a veces no"). Acá usamos el
   * rectángulo real del sprite: feetY anclado abajo, sube BODY_FRAME_HEIGHT,
   * ancho ~1 tile. Entre varios candidatos cuya caja contiene el punto, elige el
   * de pies más cercanos al click (el que está "adelante" visualmente). NPCs
   * tienen prioridad en empate (se iteran primero).
   */
  findEntityAtWorld(worldX: number, worldY: number): { type: 'player' | 'npc'; id: string } | null {
    const HALF_W = TILE_SIZE * 0.55; // ~18px: algo más que medio tile (tolera armaduras anchas)
    // Offsets del body respecto a los pies (currentWorldY), ver addOrUpdateNpc:
    // bodyBottomY = feetY + BODY_OFFSET_Y + BODY_FRAME_HEIGHT/2 (borde inferior, origin Y=1).
    const bottomOffset = GameScene.BODY_OFFSET_Y + GameScene.BODY_FRAME_HEIGHT / 2;
    const topOffset = bottomOffset - GameScene.BODY_FRAME_HEIGHT;

    let best: { type: 'player' | 'npc'; id: string; feetY: number } | null = null;
    const consider = (type: 'player' | 'npc', id: string, ex: number, ey: number): void => {
      if (worldX < ex - HALF_W || worldX > ex + HALF_W) return;
      if (worldY < ey + topOffset || worldY > ey + bottomOffset) return;
      if (!best || Math.abs(ey - worldY) < Math.abs(best.feetY - worldY)) {
        best = { type, id, feetY: ey };
      }
    };

    // PRIORIDAD del jugador local: si el clic cae sobre TU propio sprite, te
    // seleccionás a vos. Antes, con vos y un NPC lado a lado (mismo feetY) y el
    // clic en la zona de solape de las cajas, el desempato (`<` estricto, NPCs
    // iterados primero) dejaba ganar al NPC → "clic sobre mí seleccionaba al lobo
    // de al lado". El self-target sigue habilitado; solo le damos prioridad.
    if (this.scene.localSessionId) {
      const lr = this.scene.playerObjects.get(this.scene.localSessionId);
      if (
        lr &&
        worldX >= lr.currentWorldX - HALF_W &&
        worldX <= lr.currentWorldX + HALF_W &&
        worldY >= lr.currentWorldY + topOffset &&
        worldY <= lr.currentWorldY + bottomOffset
      ) {
        return { type: 'player', id: this.scene.localSessionId };
      }
    }

    // NPCs primero (prioridad en empate), luego el resto de players (sin el local,
    // ya cubierto arriba).
    for (const [id, render] of this.scene.npcObjects) {
      consider('npc', id, render.currentWorldX, render.currentWorldY);
    }
    for (const [id, render] of this.scene.playerObjects) {
      if (id === this.scene.localSessionId) continue;
      consider('player', id, render.currentWorldX, render.currentWorldY);
    }
    return best ? { type: best.type, id: best.id } : null;
  }

  /**
   * Lee localPlayer.targetId desde el schema y aplica si cambió.
   */
  syncServerTarget(): void {
    if (!this.scene.localSessionId || !this.scene.room) return;
    const localPlayer = this.scene.room.state.players.get(this.scene.localSessionId) as PlayerStateView | undefined;
    if (!localPlayer) return;
    const serverTargetId = localPlayer.targetId ?? '';
    if (serverTargetId !== this.lastObservedTargetId) {
      this.lastObservedTargetId = serverTargetId;
      this.applyServerTarget(serverTargetId);
    } else if (serverTargetId !== '' && this.selectedTargetType === null) {
      // El server confirmó hace varios frames pero todavía no teníamos type resuelto
      // (race entre llegada del target y rendering del entity). Reintentar.
      this.applyServerTarget(serverTargetId);
    }
  }

  /**
   * Reposiciona el reticle a los pies del target. Llamado por frame.
   * Si el target desaparece (salió de visión / cambio de mapa), limpia.
   */
  updateTargetReticle(): void {
    if (!this.selectedTargetId || !this.selectedTargetType) return;

    const data = this.readTargetSnapshot();
    if (!data) {
      // Target ya no está en state.players/npcs (despawn / out-of-vision).
      // El server-side validatePlayerTargets lo va a limpiar en el próximo tick (~200ms).
      // Acá NO mandamos otro SELECT_TARGET: dejamos que el schema nos sincronice.
      return;
    }

    if (!this.targetReticle) this.ensureTargetReticle(data.bodyId);
    if (!this.targetReticle) return;

    // Sólo rescalar cuando bodyId cambia (= equipa armadura, o cambio de target).
    // Llamarlo cada frame mataría el tween de pulso y reiniciaría desde cero
    // cada frame → pulso roto + scale creep.
    if (data.bodyId !== this.lastReticleBodyId) {
      this.lastReticleBodyId = data.bodyId;
      this.rescaleTargetReticle(data.bodyId);
    }

    // Posicionar igual que el dropshadow real del entity: a los pies del body.
    // ANTI-VIBRATION: redondear a píxel entero para alinear con el sprite del
    // entity (que también usa Math.round en updateNPCsPositions / addOrUpdatePlayer).
    // Sin esto, retículo y sprite quedan desfasados ~0.3px cada frame → flicker.
    const renderX = Math.round(data.worldX);
    const renderY = Math.round(data.worldY);
    const bodyBottomY = renderY + GameScene.BODY_OFFSET_Y + GameScene.BODY_FRAME_HEIGHT / 2;
    this.targetReticle.setPosition(renderX, bodyBottomY + GameScene.DROPSHADOW_OFFSET_Y);

    // Depth: idéntica fórmula y source que el dropshadow del entity (server tile),
    // para que ambos snapeen atómicamente al cruzar tile boundary. El intento
    // anterior de leer entityDropshadow.depth quedaba 1 frame atrás (updateTargetReticle
    // corre ANTES que el sync per-frame del dropshadow del entity en la misma update).
    // Usando serverTileY directo, no hay race — ambos calculan desde el mismo source.
    const baseDepth = this.scene.getEntityBaseDepthFromTileY(data.serverTileY);
    this.targetReticle.setDepth(baseDepth + GameScene.SHADOW_DEPTH_DELTA + 0.005);
  }

  /**
   * Empuja HP en vivo + estado al panel. Llamado por frame.
   */
  updateTargetPanelLive(): void {
    if (!this.selectedTargetId || !this.targetPanel?.isVisible()) return;
    const data = this.readTargetSnapshot();
    if (!data) return;

    if (data.state === EntityState.DEAD) {
      // Decisión del usuario: NO limpiar target al morir, swap head a casper.
      this.targetPanel.setHeadCasper();

      // Auto-retarget: si la preferencia está ON, ciclar al siguiente enemigo visible.
      // Guard para no spamear SELECT_TARGET cada frame mientras el target sigue muerto.
      // El auto-retarget es SOLO para "murió el enemigo que tenía fijado": nunca debe
      // dispararse si el target soy yo mismo (self-target) ni si YO estoy muerto. Sin
      // esta guarda, focusearte estando muerto te enganchaba al hostil más cercano.
      const targetIsSelf = this.selectedTargetId === this.scene.localSessionId;
      if (
        this.autoRetargetOnDeath &&
        !targetIsSelf &&
        !this.scene.isLocalPlayerDead() &&
        this.lastAutoRetargetFor !== this.selectedTargetId
      ) {
        this.lastAutoRetargetFor = this.selectedTargetId;
        this.cycleTargetEnemy(false);
      }
    } else if (this.selectedTargetType === 'player') {
      this.targetPanel.setHp(data.currentHp, data.maxHp);
    }
    // TODO: limpiar target al cambiar de mapa cuando exista el sistema de map-change runtime.
  }

  /**
   * Devuelve las coordenadas tile del target actual (para auto-centrar AoE).
   */
  getTargetWorldTile(): { x: number; y: number } | null {
    const data = this.readTargetSnapshot();
    if (!data) return null;
    return {
      x: Math.floor(data.worldX / TILE_SIZE),
      y: Math.floor(data.worldY / TILE_SIZE),
    };
  }

  /**
   * Tab / Shift+Tab: cicla al siguiente / anterior NPC attackable visible.
   * Ordena por distancia ascendente al local player. Wraparound al final/principio.
   * Si no hay enemigos visibles, no hace nada.
   */
  cycleTargetEnemy(reverse: boolean): void {
    if (!this.scene.localSessionId || !this.scene.room) return;
    // Un muerto no cicla enemigos con Tab (sólo puede targetear al sacerdote por click).
    if (this.scene.isLocalPlayerDead()) return;
    const localRender = this.scene.playerObjects.get(this.scene.localSessionId);
    if (!localRender) return;

    const playerTileX = Math.floor(localRender.currentWorldX / TILE_SIZE);
    const playerTileY = Math.floor(localRender.currentWorldY / TILE_SIZE);

    // Build lista de NPCs attackable visibles vivos.
    // Usa el half-range shared (mismo que el filter AOI del server).
    const candidates: Array<{ id: string; dist2: number }> = [];
    for (const [id, render] of this.scene.npcObjects) {
      const npc = this.scene.room.state.npcs.get(id) as NPCStateView | undefined;
      if (!npc) continue;
      if ((npc.attackable ?? 0) !== 1) continue;
      if ((npc.minHp ?? 0) <= 0) continue; // muerto / sin HP

      const nx = Math.floor(render.currentWorldX / TILE_SIZE);
      const ny = Math.floor(render.currentWorldY / TILE_SIZE);
      const dx = nx - playerTileX;
      const dy = ny - playerTileY;
      if (Math.abs(dx) > VIEW_HALF_RANGE_X) continue;
      if (Math.abs(dy) > VIEW_HALF_RANGE_Y) continue;

      candidates.push({ id, dist2: dx * dx + dy * dy });
    }

    if (candidates.length === 0) return;
    candidates.sort((a, b) => a.dist2 - b.dist2);

    // Posición del current target en la lista.
    let currentIdx = -1;
    if (this.selectedTargetId) {
      currentIdx = candidates.findIndex((c) => c.id === this.selectedTargetId);
    }

    const N = candidates.length;
    const nextIdx = currentIdx === -1
      ? 0
      : ((currentIdx + (reverse ? -1 : 1)) % N + N) % N;

    this.setTarget('npc', candidates[nextIdx].id);
  }
}
